/**
 * "A new version is available" — and, when it is provably safe, the reload itself.
 *
 * See `lib/versionCheck.ts` for why this exists and for the bounds that stop it looping. This
 * component is the wiring: poll the served version manifest, confirm a mismatch before believing it,
 * offer a reload, and take the automatic path only at a navigation boundary with no work in flight.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  BUILD_RELEASE,
  compareReleases,
  fetchServedRelease,
  hasAlreadyAutoReloadedFor,
  hasUnsavedWork,
  markAutoReloadedFor,
  shouldAutoReload,
} from '../lib/versionCheck.js';

/**
 * How often to ask.
 *
 * Five minutes against a static JSON file served with `no-store`. This used to poll `/health`, which
 * cross-model review pointed out runs a DocumentDB round trip and a session-store check on every
 * call — one reviewer with a tab open for a working day would have been 96 needless health probes of
 * both dependencies, to answer a question the static file answers for free.
 */
const POLL_INTERVAL_MS = 5 * 60_000;

/**
 * How long a mismatch must persist before it is believed.
 *
 * Deliberately expressed in elapsed time rather than "two polls": a visibility change also triggers
 * a poll, so counting polls can be satisfied by two tab switches a second apart — over a directory
 * that is still being written. Thirty seconds comfortably outlasts a `vite build` writing into
 * `dist/`, and delaying a banner by half a minute costs nothing.
 */
const CONFIRM_MIN_ELAPSED_MS = 30_000;

/**
 * The manual reload, which is NOT simply `window.location.reload()`.
 *
 * Found by cross-model review: the automatic path is fenced by {@link shouldAutoReload} and the
 * button went straight past all of it, so a reviewer with a half-written comment who clicked the
 * banner lost it with no warning — the exact harm the "notify, do not force" decision was taken to
 * avoid, re-entering through the button meant to be the safe option.
 *
 * A confirm rather than a disabled button, deliberately, and this is a considered departure from the
 * review's suggested fix: a reviewer who wants the new build while holding a draft they do not care
 * about should be able to say so. Disabling the control would leave them stuck on an old build with
 * no way forward except finishing work they may have already abandoned.
 */
function reloadDeliberately(): void {
  if (hasUnsavedWork()) {
    const proceed = window.confirm(
      'You have unsaved work on this screen — a comment, a note, or an edit. Reloading will discard it. Reload anyway?',
    );
    if (!proceed) {
      return;
    }
  }
  window.location.reload();
}

export function VersionUpdateBanner() {
  // Only set once the SAME mismatching release has been seen twice in a row.
  const [confirmedRelease, setConfirmedRelease] = useState<string | null>(null);
  const location = useLocation();
  const pendingRelease = useRef<{ release: string; seenAt: number } | null>(null);
  // One fetch at a time. Without this, two quick tab switches fire two overlapping polls and the
  // "two consecutive observations" rule is satisfied within seconds — over a `dist/` that is still
  // being rewritten, which is precisely the state the rule exists to sit out. Found by cross-model
  // review of the diff.
  const inFlight = useRef(false);
  // The reload decision reads the freshest confirmed release without making the polling effect
  // depend on it, which would tear down and rebuild the interval on every poll.
  const confirmedReleaseRef = useRef<string | null>(null);
  confirmedReleaseRef.current = confirmedRelease;

  useEffect(() => {
    // A build with no release baked in (local dev) has nothing to compare, so it never polls at all.
    if (BUILD_RELEASE === '') {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();

    const poll = () => {
      // Asking while the tab is hidden costs a request nobody is waiting on. The reviewer cannot see
      // a banner they are not looking at, and the visibility listener below catches them coming back.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      void fetchServedRelease(controller.signal)
        .then((release) => {
          if (cancelled || release === null) {
            // A failed read is not evidence of anything. An already-confirmed banner stays up; a
            // pending observation is left alone rather than being promoted on missing information.
            return;
          }
          if (release === BUILD_RELEASE) {
            // We are current. Clear both, so a transient mid-rewrite reading cannot accumulate
            // towards a confirmation across an hour of polls.
            pendingRelease.current = null;
            setConfirmedRelease(null);
            return;
          }
          // **Two observations of the SAME release, separated by real time, before we believe it.**
          // A deploy rewrites `dist/` in place while Caddy is serving it, so a single read can catch
          // a directory mid-write. The elapsed-time condition is what makes this patience rather
          // than bookkeeping: without it, two rapid tab switches confirm within seconds.
          const previous = pendingRelease.current;
          if (previous?.release === release && Date.now() - previous.seenAt >= CONFIRM_MIN_ELAPSED_MS) {
            setConfirmedRelease(release);
          } else if (previous?.release !== release) {
            pendingRelease.current = { release, seenAt: Date.now() };
          }
        })
        .finally(() => {
          inFlight.current = false;
        });
    };

    poll();
    const timer = setInterval(poll, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        poll();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // The automatic path. Keyed on `location.pathname`, so it can only fire as the reviewer moves
  // between screens — never mid-edit, and never while they are sitting on one item.
  useEffect(() => {
    const release = confirmedReleaseRef.current;
    if (!release) {
      return;
    }
    const { updateAvailable } = compareReleases(BUILD_RELEASE, release);
    if (
      !shouldAutoReload({
        updateAvailable,
        deployedRelease: release,
        atNavigationBoundary: true,
        hasUnsavedWork: hasUnsavedWork(),
        alreadyReloadedForRelease: hasAlreadyAutoReloadedFor(release),
      })
    ) {
      return;
    }
    // **Recorded BEFORE reloading, and only reload if the record actually stuck.** If the marker
    // cannot be persisted — full storage, a private mode that silently discards writes — the next
    // mount would see no marker and reload again, forever. A tab that cannot remember it tried does
    // not get to try.
    if (!markAutoReloadedFor(release)) {
      return;
    }
    window.location.reload();
  }, [location.pathname]);

  const { updateAvailable } = compareReleases(BUILD_RELEASE, confirmedRelease);
  if (!updateAvailable) {
    return null;
  }

  return (
    <div
      className="flex items-center justify-between gap-4 border-b border-amber-200 bg-amber-50 px-8 py-2 text-sm text-amber-900"
      role="status"
    >
      <span>
        A newer version of the authoring tool has been deployed. This tab is still running the older
        one.
      </span>
      <button
        className="shrink-0 rounded bg-amber-900 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-800"
        onClick={reloadDeliberately}
        type="button"
      >
        Reload now
      </button>
    </div>
  );
}
